import { useMemo } from 'react';
import { ANALYSIS_STEPS } from '../../constants/analysisSteps';
import { FlowchartStepCard } from './FlowchartStepCard';
import type { FlowchartStep } from '../../constants/analysisSteps';

interface AnalysisFlowchartProps {
  currentStepId?: number | null;
  isPreview?: boolean;
}

export function AnalysisFlowchart({ currentStepId, isPreview = false }: AnalysisFlowchartProps) {
  const steps = useMemo(() => {
    return ANALYSIS_STEPS.map((step) => {
      let status: 'pending' | 'running' | 'completed' = 'pending';

      if (!isPreview && currentStepId) {
        if (step.id < currentStepId) {
          status = 'completed';
        } else if (step.id === currentStepId) {
          status = 'running';
        }
      }

      return { ...step, status };
    });
  }, [currentStepId, isPreview]);

  const completedCount = steps.filter((s) => s.status === 'completed').length;
  const totalSteps = steps.length;
  const progressPercent = Math.round((completedCount / totalSteps) * 100);

  return (
    <div className="space-y-3">
      {/* Progress bar */}
      {!isPreview && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-700">
              Analysis Progress
            </span>
            <span className="text-xs font-mono text-gray-600">
              {completedCount}/{totalSteps} steps ({progressPercent}%)
            </span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 italic">
            Estimated time remaining: {currentStepId ? '10-30s' : '20-40s'}
          </p>
        </div>
      )}

      {/* Steps list */}
      <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
        {steps.map((step, idx) => (
          <FlowchartStepCard
            key={step.id}
            step={step}
            status={step.status}
            index={idx + 1}
          />
        ))}
      </div>

      {/* Preview notice */}
      {isPreview && (
        <div className="p-2 bg-blue-50 rounded border border-blue-200">
          <p className="text-xs text-blue-700">
            💡 This flowchart shows the complete analysis pipeline. Each step will be
            checked off as it completes during analysis.
          </p>
        </div>
      )}
    </div>
  );
}
